import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';
import { NavigationService } from './navigation.service';
import { AppUpdateService } from './app-update.service';
import { Platform } from '@ionic/angular/standalone';
import { AppState } from '../store/app.state';
import { AlertController } from '@ionic/angular';
import { PreferencesService } from './preferences.service';

interface CustomAUTWindow {
  cypressNavigateByUrl: (url: string) => void;
  triggerWebUpdateModal: (url: string) => void;
  setNativeAppUpdate: () => void;
  fakeAppResume: () => void;
}

@Injectable({ providedIn: 'root' })
export class DeveloperToolsService {
  constructor(
    private navigationService: NavigationService,
    private ngZone: NgZone,
    private appUpdateService: AppUpdateService,
    private platform: Platform,
    private appState: AppState,
    private alertController: AlertController,
    private preferenceService: PreferencesService,
  ) {}

  initialize(): void {
    void this.triggerLogoutOnEnvChange();
  }

  initializeCypress(): void {
    if (environment.envName === 'prod' && window.location.hostname !== 'localhost') {
      return;
    }

    (window as unknown as CustomAUTWindow).cypressNavigateByUrl = (url: string): void => this.cypressNavigateByUrl(url);
    (window as unknown as CustomAUTWindow).triggerWebUpdateModal = (url: string): void => this.triggerWebUpdateModal(url);
    (window as unknown as CustomAUTWindow).setNativeAppUpdate = (): void => this.setNativeAppUpdate();
    (window as unknown as CustomAUTWindow).fakeAppResume = (): void => this.fakeAppResume();
  }

  private async triggerLogoutOnEnvChange(): Promise<void> {
    // Never call this in the production url
    if (window.location.hostname === 'booksmart.worldreader.org') {
      return;
    }

    if (environment.envName !== this.appState.app$.value.env) {
      const alert = await this.alertController.create({
        header: 'Environment changed',
        message: 'The app will be reloaded, please login again',
        backdropDismiss: false,
        htmlAttributes: { 'data-cy': 'environment-changed' },
        buttons: [
          {
            htmlAttributes: { 'data-cy': 'environment-changed-ok' },
            text: 'OK',
            handler: async (): Promise<void> => {
              await this.preferenceService.clear();
              window.location.reload();
            },
          },
        ],
      });

      await alert.present();
    }
  }

  private setNativeAppUpdate(): void {
    void this.appUpdateService.initializeNative();
  }

  private cypressNavigateByUrl(url: string): void {
    this.ngZone.run(() => {
      void this.navigationService.navigateRoot(url);
    });
  }

  private triggerWebUpdateModal(url: string): void {
    void this.appUpdateService.updateWebApp(url);
  }

  private fakeAppResume(): void {
    this.platform.resume.next();
  }
}
